// Canonical quantity and recipe validation shared by web, native and server.
export const emptyRecipe = () => ({ name: '', basis: 'volume', lines: [] });
export const emptyMeasurements = () => ({ shape: 'triangular', length: '', width: '', height: '', topWidth: '', density: '', measuredVolume: '', measuredTons: '' });
export const emptyPlan = () => ({ recipe: emptyRecipe(), measurements: emptyMeasurements() });
export function validateRecipe(recipe, required = false) {
    if (!recipe || !['volume', 'mass'].includes(recipe.basis) || !Array.isArray(recipe.lines))
        throw new Error('Choose a percentage basis and ingredients.');
    if (recipe.lines.length === 0) {
        if (required)
            throw new Error('Add at least one ingredient.');
        return;
    }
    if (recipe.lines.length > 30)
        throw new Error('Use at most 30 ingredients.');
    const seen = new Set();
    let total = 0;
    for (const line of recipe.lines) {
        if (!Number.isInteger(line.ingredientId) || line.ingredientId < 1 || seen.has(line.ingredientId))
            throw new Error('Choose each ingredient once.');
        seen.add(line.ingredientId);
        if (typeof line.percent !== 'number' || !Number.isFinite(line.percent) || line.percent <= 0 || line.percent > 100)
            throw new Error('Every ingredient needs a percentage above zero.');
        total += line.percent;
    }
    if (Math.abs(total - 100) > 0.001)
        throw new Error(`Ingredient percentages must total 100% (currently ${Number(total.toFixed(2))}%).`);
}
function optional(value, label, max = 1e9) { if (value === '' || value == null)
    return null; if (typeof value !== 'string' && typeof value !== 'number')
    throw new Error(`Check ${label}.`); const n = Number(value); if (!Number.isFinite(n) || n <= 0 || n > max)
    throw new Error(`${label} must be a positive number.`); return n; }
export function calculateQuantities(m) {
    if (!m || !['triangular', 'trapezoidal', 'cone'].includes(m.shape))
        throw new Error('Choose a supported physical shape.');
    const length = optional(m.length, 'Length', 100000), width = optional(m.width, 'Width', 100000), height = optional(m.height, 'Height', 10000), density = optional(m.density, 'Density', 5000), measuredVolume = optional(m.measuredVolume, 'Measured volume'), measuredTons = optional(m.measuredTons, 'Measured weight');
    let calculatedVolume = null;
    const anyDimensions = length !== null || width !== null || height !== null;
    if (anyDimensions) {
        if (width === null || height === null || (m.shape !== 'cone' && length === null))
            throw new Error('Enter the width, height and windrow length to calculate volume.');
        if (m.shape === 'cone')
            calculatedVolume = Math.PI * (width / 2) ** 2 * height / 3 / 27;
        else if (m.shape === 'triangular')
            calculatedVolume = length * width * height / 2 / 27;
        else {
            const top = m.topWidth === '' ? null : Number(m.topWidth);
            if (top === null || !Number.isFinite(top) || top < 0 || top > width)
                throw new Error('Top width must be between zero and the base width.');
            calculatedVolume = length * height * (width + top) / 2 / 27;
        }
    }
    const volume = measuredVolume ?? calculatedVolume;
    const tons = measuredTons ?? (volume !== null && density !== null ? volume * 27 * density / 2000 : null);
    return { volume, tons, calculatedVolume, volumeSource: measuredVolume !== null ? 'measured' : calculatedVolume !== null ? 'estimated' : 'unknown', weightSource: measuredTons !== null ? 'measured' : tons !== null ? 'estimated' : 'unknown' };
}
